<template>
  <div v-if="isLoaded" class="register-page">
    <modal-wrapper v-if="showModal">
      <modal-container :show-close="false">
        <img
          src="@/assets/img/logo.svg"
          class="register-page__modal-logo"
          width="100"
          height="40"
        />
        <div class="register-page__modal-heading">
          {{ $t('application-form.modal.header', {resident_status: $t(`application-form.modal.${residentStatus}`)}) }}
        </div>
        <div class="register-page__modal-text">
          {{ $t('application-form.modal.text', {lang: $t(langName)}) }}
        </div>
        <pc-button variant="dark" @click="showModal = false">
          {{ $t('application-form.modal.button') }}
        </pc-button>
      </modal-container>
    </modal-wrapper>
    <modal-wrapper v-if="showHasNoCitizenModal">
      <modal-container :show-close="true" @close="closeHasNoCitizenModal">
        <div class="citizen-modal">
          <div
            class="citizen-modal__info"
            v-html="$t('notifications.citizen-modal-title')"
          >
          </div>
          <div class="citizen-modal__sub-info">
            {{ $t('notifications.citizen-modal-text') }}
          </div>
          <icu-contacts />
        </div>
      </modal-container>
    </modal-wrapper>
    <modal-wrapper v-if="showBlackListResidency">
      <modal-container :show-close="true" @close="closeHasNoCitizenModal">
        <div class="citizen-modal">
          <div
            class="citizen-modal__info"
            v-html="$t('notifications.residency-modal-title')"
          >
          </div>
          <div class="citizen-modal__sub-info">
            {{ $t('notifications.residency-modal-text') }}
          </div>
          <icu-contacts />
        </div>
      </modal-container>
    </modal-wrapper>
    <non-resident-modal v-if="showNonResidentModal"></non-resident-modal>
    <icu-support>
      {{ $t('common.if-you-have-questions') }}
      <a href="mailto:support@icu.ua" target="_blank">support@icu.ua</a>
    </icu-support>
    <status-bar
      :show-back-button="currentStepNumber > 1"
      :width="statusBarWidth"
      :title="$t(currentStep.title)"
      @back="previousStep"
    />
    <validation-observer
      ref="observer"
      v-slot="{invalid, valid, errors}"
      tag="div"
      class="register-page-form"
    >
      <component
        :is="currentStep.component"
        :step-form-data="currentFormData"
        :is-older-than45="isOlderThan45"
        :backend-errors="backendErrors"
        :passport-choices="passportChoices"
        :settings="appSettings"
        :is-resident="isResident"
        :is-citizen="isCitizen"
        :citizenship="citizenship"
        :countries="countries"
        :black-list-countries="blackListCountries"
        :is-valid="valid"
        @add-card="addCard"
        @delete-card="deleteCard"
        @update-field="updateField"
        @delete-array-item="deleteArrayItem"
        @add-file="addFile"
        @delete-file="deleteFile"
      />
      <div class="register-page-form-actions">
        <pc-button
          v-if="showAddAccountButton && valid"
          variant="void"
          icon-left-name="plus"
          @click="addAccount"
        >
          {{ $t('application-form.add-another-account') }}
        </pc-button>
        <div v-if="isLastStep" class="register-page-form-actions__hint">
          {{ $t('application-form.press-to-send') }}
        </div>
        <pc-button variant="dark" :disabled="invalid" @click="nextStep">
          {{ $t(currentStep.nextButtonText) }}
        </pc-button>
      </div>
    </validation-observer>
    <icu-loader v-if="isLoading" />
  </div>
</template>

<script>
import StatusBar from '@/components/views/RegisterPage/StatusBar.vue'
import ModalWrapper from '@/components/modals/ModalWrapper'
import ModalContainer from '@/components/modals/ModalContainer'
import CitizenshipStep from '@/components/views/RegisterPage/steps/CitizenshipStep'
import FirstStep from '@/components/views/RegisterPage/steps/FirstStep.vue'
import nonResidentFirstStep from '@/components/views/RegisterPage/steps/non-resident/FirstStep.vue'
import PcButton from '@/components/common/PcButton.vue'
import FifthStep from '@/components/views/RegisterPage/steps/FifthStep.vue'
import FourthStep from '@/components/views/RegisterPage/steps/FourthStep.vue'
import ThirdStep from '@/components/views/RegisterPage/steps/ThirdStep.vue'
import SecondStep from '@/components/views/RegisterPage/steps/SecondStep.vue'
import IcuLoader from '@/components/common/IcuLoader.vue'
import { ValidationObserver } from 'vee-validate'
import { makePreviewFromImage } from '@/utils.js'
import { cloneDeep, get, setWith } from 'lodash'
import { constants } from '@/constants.js'
import IcuIcon from '@/components/common/IcuIcon.vue'
import { differenceInYears, format, parse } from 'date-fns'
import IcuSupport from '@/components/common/IcuSupport.vue'
import { api } from '@/api/api.js'
import StepFatca from '@/components/views/RegisterPage/steps/StepFatca.vue'
import '@/rules/RegisterPage/resident-rules.js'
import '@/rules/RegisterPage/non-resident-rules.js'
import countries from 'i18n-iso-countries'
import { v4 as uuidv4 } from 'uuid'
import NonResidentModal from '@/components/modals/NonResidentModal'
import IcuContacts from '@/components/common/IcuContacts'
import {mapGetters} from 'vuex'
import {onBeforePageUnload} from '@/helpers'

export default {
  name: 'RegisterPage',
  components: {
    IcuContacts,
    NonResidentModal,
    ModalContainer,
    ModalWrapper,
    IcuSupport,
    IcuIcon,
    IcuLoader,
    PcButton,
    StatusBar,
    ValidationObserver
  },
  fileFieldNames: [
    'photo_inn',
    'photo',
    'photo_45',
    'photo_register',
    'photo_migrant_reference',
    'doc',
    'photo_selfie'
  ],
  deepFieldNames: [
    'registration_address',
    'living_address',
  ],
  countries,
  uuidv4,
  fieldsByPassport: {
    [constants.PASSPORT_TYPES.CARD_ID]: {
      passport_type: constants.PASSPORT_TYPES.CARD_ID,
      country: 'UA',
      number: '',
      date_issued: '',
      issued: '',
      valid_until: '',
      reestr_number: '',
      photo: [],
      photo_selfie:[],
      photo_register: []
    },
    [constants.PASSPORT_TYPES.INTERNATIONAL_PASSPORT]: {
      passport_type: constants.PASSPORT_TYPES.INTERNATIONAL_PASSPORT,
      country: 'UA',
      seria: '',
      number: '',
      date_issued: '',
      issued: '',
      valid_until: '',
      photo: [],
      photo_register: [],
      photo_selfie:[],
      is_biometric: false
    },
    [constants.PASSPORT_TYPES.OLD_PASSPORT]: {
      passport_type: constants.PASSPORT_TYPES.OLD_PASSPORT,
      country: 'UA',
      seria: '',
      number: '',
      date_issued: '',
      issued: '',
      photo: [],
      photo_45: [],
      photo_selfie:[]
    },


    [constants.PASSPORT_TYPES.TEMPORARY_RESIDENCE]: {
      passport_type: constants.PASSPORT_TYPES.TEMPORARY_RESIDENCE,
      country:'UA',
      seria: '',
      number: '',
      date_issued: '',
      valid_until: '',
      issued: '',
      photo: [],
      photo_selfie:[]
    },
    [constants.PASSPORT_TYPES.TEMPORARY_PERMIT]: {
      passport_type: constants.PASSPORT_TYPES.TEMPORARY_PERMIT,
      country:'UA',
      seria: '',
      number: '',
      date_issued: '',
      valid_until: '',
      issued: '',
      photo: [],
      photo_selfie:[]
    },
    [constants.PASSPORT_TYPES.TEMPORARY_CERTIFICATE]: {
      passport_type: constants.PASSPORT_TYPES.TEMPORARY_CERTIFICATE,
      country:'UA',
      seria: '',
      number: '',
      date_issued: '',
      valid_until: '',
      issued: '',
      photo: [],
      photo_selfie:[]
    },
    [constants.PASSPORT_TYPES.ADDITIONAL_PROTECTION_CARD]: {
      passport_type: constants.PASSPORT_TYPES.ADDITIONAL_PROTECTION_CARD,
      country:'UA',
      seria: '',
      number: '',
      date_issued: '',
      valid_until: '',
      issued: '',
      photo: [],
      photo_selfie:[]
    },
    [constants.PASSPORT_TYPES.PERMANENT_RESIDENCE]: {
      passport_type: constants.PASSPORT_TYPES.PERMANENT_RESIDENCE,
      country: 'UA',
      seria: '',
      number: '',
      date_issued: '',
      valid_until: '',
      issued: '',
      photo: [],
      photo_selfie:[]
    },
    [constants.PASSPORT_TYPES.FOREIGN_TEMPORARY_RESIDENCE]:{
      passport_type: constants.PASSPORT_TYPES.FOREIGN_TEMPORARY_RESIDENCE,
      country: null,
      seria: '',
      number: '',
      date_issued: '',
      valid_until: '',
      issued: '',
      photo: [],
      photo_selfie:[]
    },
    [constants.PASSPORT_TYPES.FOREIGN_PERMANENT_RESIDENCE]:{
      passport_type: constants.PASSPORT_TYPES.FOREIGN_PERMANENT_RESIDENCE,
      country: null,
      seria: '',
      number: '',
      date_issued: '',
      valid_until: '',
      issued: '',
      photo: [],
      photo_selfie:[]
    },
    [constants.PASSPORT_TYPES.FOREIGN_CARD_ID]:{
      passport_type: constants.PASSPORT_TYPES.FOREIGN_CARD_ID,
      country: null,
      seria: '',
      number: '',
      date_issued: '',
      valid_until: '',
      issued: '',
      photo: [],
      photo_selfie:[]
    },
    [constants.PASSPORT_TYPES.FOREIGN_INTERNATIONAL_PASSPORT]:{
      passport_type: constants.PASSPORT_TYPES.FOREIGN_INTERNATIONAL_PASSPORT,
      country: null,
      seria: '',
      number: '',
      date_issued: '',
      valid_until: '',
      issued: '',
      photo: [],
      photo_selfie:[]
    },
    [constants.PASSPORT_TYPES.FOREIGN_PASSPORT]:{
      passport_type: constants.PASSPORT_TYPES.FOREIGN_PASSPORT,
      country:null,
      seria: '',
      number: '',
      date_issued: '',
      valid_until: '',
      issued: '',
      photo: [],
      photo_selfie:[]

    }
  },

  accountDataTemplate: {
    iban: '',
    swift:'',
    number: '',
    name: '',
    additional_information:'',
    currency: [],
    doc: [],
    cards:[]
  },

  cardDataTemplate:{
    card_number: null,
    card_expire: null,
    doc:[]
  },

  fatca: {
    is_usa_citizen: false,
    title: false,
    has_residence: false,
    is_usa_born: false,
    has_usa_address: false,
    has_usa_phone: false,
    has_usa_payment_order: false,
    has_usa_p_a: false,
    tin_code: null
  },

  data() {
    return {
      isLoaded: false,
      currentStepNumber: 0,
      countries: [],
      isResident: true,
      citizenship:'',
      showModal: false,
      showHasNoCitizenModal: false,
      showBlackListResidency: false,
      showNonResidentModal: false,
      blackListCountries:[],
      formDataByStep: {
        0: {
          citizenship: null,
          residency: null,
          employment: {
            type: '',
            income_level: ''
          }
        },
        1: {
          last_name: null,
          first_name: null,
          middle_name: '',
          birth_date: null,
          birth_city: null,
          citizenship: null,
          inn: null,
          photo_inn: [],
          has_inn: null,
        },
        2: {
          passport_type: null,
          seria: '',
          number: '',
          date_issued: '',
          issued: '',
          valid_until: '',
          reestr_number: '',
          photo: [],
          photo_register: [],
          photo_45: [],
          photo_selfie: []
        },
        3: {
          registration_address: '',
          living_address: '',
          registration_is_living: false,
          is_internal_migrant: false,
          photo_migrant_reference: [],
          photo_register: []
        },
        5: {
          is_usa_citizen: false,
          has_residence: false,
          is_usa_born: false,
          has_usa_address: false,
          has_usa_phone: false,
          has_usa_payment_order: false,
          has_usa_p_a: false,
          tin_code: ''
        },
        6: {
          target: [],
          frequency: null
        }
      },
      loadedSteps: [],
      isLoading: false,
      isOlderThan45: false,
      backendErrors: {},
      passportChoices: [],
    }
  },
  computed: {
    ...mapGetters(['appSettings']),
    steps() {
      return {
        0: {
          component: CitizenshipStep,
          title: 'application-form.step-0.title',
          nextButtonText: 'application-form.step-0.register',
        },
        1: {
          component: this.isResident ? FirstStep : nonResidentFirstStep ,
          title: 'application-form.step-1.title',
          nextButtonText: 'application-form.continue',
        },
        2: {
          component: SecondStep,
          title: 'application-form.step-2.title',
          nextButtonText: 'application-form.continue',
        },
        3: {
          component: ThirdStep,
          title: 'application-form.step-3.title',
          nextButtonText: 'application-form.continue',
        },
        4: {
          component: FourthStep,
          title: 'application-form.step-4.title' ,
          nextButtonText: 'application-form.continue',
        },
        5: {
          component: StepFatca,
          title: 'application-form.step-5.title',
          nextButtonText: 'application-form.continue',
        },
        6: {
          component: FifthStep,
          title: 'application-form.step-6.title',
          nextButtonText: 'application-form.i-confirm',
        },
      }
    },
    statusBarWidth() {
      return Math.ceil(this.currentStepNumber / Object.keys(this.steps).length * 100)
    },
    showAddAccountButton() {
      return this.currentStepNumber === 4 && this.formDataByStep[4].length < this.appSettings.MAX_ACCOUNTS_COUNT
    },
    currentStep() {
      return this.steps[this.currentStepNumber]
    },
    currentFormData() {
      return this.formDataByStep[this.currentStepNumber]
    },
    isLastStep() {
      return this.currentStepNumber === Object.keys(this.formDataByStep).length
    },
    processedCurrentFormData() {
      let processedFormData

      if (Array.isArray(this.currentFormData)) {
        processedFormData = []
        this.currentFormData.forEach(arrayItem => {
          processedFormData.push(this.processFormDataObject(arrayItem))
        })
      } else {
        processedFormData = this.processFormDataObject(this.currentFormData)
      }

      return processedFormData
    },
    residentStatus() {
      return this.isResident ? 'resident' : 'nonresident'
    },
    isCitizen(){
      return this.citizenship === 'UA'
    },
    langName() {
      return `application-form.modal.lang.${ this.isResident ? 'ua' : 'en'}`
    },
  },
  watch: {
    '$route.params.lang'(){
      this.getLocaleCountries()
    },
    currentStepNumber: {
      handler(newStep) {
        this.loadStep(newStep)
      }
    },
    'currentFormData.registration_is_living': {
      handler() {
        this.$set(this.formDataByStep[3], 'living_address', '')
      },
      immediate: true
    },
    'currentFormData.birth_date': {
      handler(newDate) {
        if (newDate) {
          const date = parse(newDate, 'yyyy-MM-dd', new Date())
          const age = differenceInYears(new Date(), date)
          this.isOlderThan45 = age >= 45
        }
      },
      immediate: true
    },
    'currentFormData.passport_type': {
      handler(newTypeCodeName, oldTypeCodeName) {
        if (newTypeCodeName && oldTypeCodeName) {
          const newFormData =  cloneDeep(this.$options.fieldsByPassport[newTypeCodeName])
          newFormData.country = this.citizenship
          this.$set(this.formDataByStep, this.currentStepNumber, newFormData)
        }
      },
      immediate: true
    },
    'currentFormData.date_issued'(newVal) {
      const isValidUntilInForm = 'valid_until' in this.currentFormData

      if (newVal && isValidUntilInForm) {
        const newDate = new Date(newVal)
        newDate.setFullYear(newDate.getFullYear() + 10)

        this.currentFormData.valid_until = format(newDate, 'yyyy-MM-dd')
      }
    }
  },

  async mounted() {
    try {
      const res = await api.auth.userInfo()
      this.$store.dispatch('setUserInfo', res.data)

      if(res.data.check_status === constants.CHECK_STATUSES.SIGNED) {
        await this.$router.push({name: 'private-cabinet'})
      }

      this.isResident = res.data.is_resident


      if(!this.isResident && this.appSettings.BLOCK_NONRESIDENT) {
        this.showNonResidentModal = true
        this.isLoaded = true
        return
      }
      this.updateLocale()
      this.getLocaleCountries()
      const blacklist = await api.profile.getCountriesBlackList()
      this.blackListCountries = blacklist.data
      this.showModal = true
    } catch(err){
      console.log(err)
      this.$toast.error(err.response.data.detail)
    }
    try {
      const {data} = await api.profile.resident['step0'].get()
      this.citizenship = data.citizenship
      this.formDataByStep[0].citizenship = data.citizenship
      this.formDataByStep[0].residency = data.residency
      if (!data.citizenship || !data.residency) {
        this.currentStepNumber = 0

      } else {
        this.currentStepNumber = this.getPageFromStorage() || 1
      }
    } catch(err) {
      this.currentStepNumber = this.getPageFromStorage()
      console.log(err)
    }
    this.isLoaded = true

   if (process.env.NODE_ENV !== 'development') {
      window.addEventListener('beforeunload', onBeforePageUnload)
    }

  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', onBeforePageUnload)
  },
  methods: {
    updateLocale(locale) {
      const formData = new FormData()
      formData.append('interface_language', this.$i18n.locale)
      api.auth.updateUserInfo(formData)
    },
    getLocaleCountries(){
      const lang = this.isResident? 'uk' : 'en'
      this.$options.countries.registerLocale(require(`i18n-iso-countries/langs/${lang}.json`))
      const countriesObj = this.$options.countries.getNames(lang)
       const countries = Object.keys(countriesObj).map(countryCode => {
        return { id: countryCode, name: countriesObj[countryCode] }
      })
      .sort((s1, s2)=> s1.name.localeCompare(s2.name))
     const indexUA = countries.findIndex((el => el.id ==='UA'))
      const countryUA = countries[indexUA]
     countries.splice(indexUA, 1)
     countries.unshift( countryUA)
     this.countries = countries

    },
    getPageFromStorage() {
      return Number(window.sessionStorage.getItem('application_page') || 0)
    },
    savePageToStorage() {
      window.sessionStorage.setItem('application_page', this.currentStepNumber)
    },
    async loadStep(stepNumber) {
      scrollTo({top: 0, left: 0, behavior: 'smooth'})
      const stepIsNotLoaded = !this.loadedSteps.includes(stepNumber)
      if (stepIsNotLoaded) {
        await this.loadStepData(stepNumber)
      }

      this.savePageToStorage()
    },
    onBeforeUnload(event) {
      event.preventDefault()
      event.returnValue = ''
    },
    previousStep() {
      if (this.currentStepNumber > 1) {
        this.currentStepNumber--
        if(this.currentStepNumber === 4) {
          this.currentStepNumber--
        }
      }
    },
    processFormDataObject(itemObj) {
      const processedFormData = {}
      const itemObjCopy = cloneDeep(itemObj)
      Object.entries(itemObjCopy).forEach(([fieldName, value]) => {
        if (fieldName === 'passport_type') {
          processedFormData[fieldName] = this.passportChoices.find(passport => passport.codename === value).codename
        } else if (this.$options.fileFieldNames.includes(fieldName)) {
          processedFormData[fieldName] = value.filter(fileData => !fileData.hasError).map(fileData => fileData.id)
        } else if (fieldName ==='cards') {
          processedFormData[fieldName] = value.filter(el => !!el.card_number)
          processedFormData.cards = value.map(card => {
            card.doc = card.doc.filter(fileData => !fileData.hasError).map(fileData => fileData.id)
            return card
          })
        } else {
          processedFormData[fieldName] = value
        }

      })

      return processedFormData
    },
    addAccount() {
      this.formDataByStep[this.currentStepNumber].push(cloneDeep(this.$options.accountDataTemplate))
      const idx = this.formDataByStep[this.currentStepNumber].length - 1

    },
    addCard(idx){
      const cardDataTemplate = cloneDeep(this.$options.cardDataTemplate)
      cardDataTemplate.id = this.$options.uuidv4()
      this.formDataByStep[4][idx].cards.push(cardDataTemplate)
    },
    deleteCard(idx, cardIndex){
      this.formDataByStep[4][idx].cards.splice(cardIndex, 1)
    },
    deleteArrayItem(formKey) {
      this.formDataByStep[this.currentStepNumber].splice(formKey, 1)
    },
    async addFile({formKey, file}) {

      let fileData = {
        _id: this.$options.uuidv4(),
        name: file.name,
        newAdded: true,
        isUploading: false,
        hasError: false,
        uploadPercent: 0,
        file
      }
      if (file.type.includes('image')) {
        fileData.src = await makePreviewFromImage(file)
      }

      const newFileArray = [
        ...get(this.formDataByStep[this.currentStepNumber], formKey),
        fileData
      ]

      this.updateField({formKey, value: newFileArray})

      if (file.type.includes('image')) {
        fileData.src = await makePreviewFromImage(file)
      }
    },
    updateField({formKey, value}) {
      setWith(this.formDataByStep[this.currentStepNumber], formKey, value, (nsValue, key, nsObject) => {
        return this.$set(nsObject, key, nsValue)
      })

      this.updateErrors(formKey, this.$options.deepFieldNames.includes(formKey) ? {} : [])
    },
    updateErrors(formKey, value) {
      setWith(this.backendErrors, formKey, value, (nsValue, key, nsObject) => {
        return this.$set(nsObject, key, nsValue)
      })
    },
    deleteFile(fileData) {
      const step = this.currentStepNumber
      const formKey = fileData.formKey
      const newFileArray = get(this.formDataByStep[step], formKey).filter(file => file._id !== fileData.file._id)
      this.updateField({formKey, value: newFileArray})
    },
    async nextStep() {
      if(this.currentStepNumber === 0) {
        this.citizenship = this.formDataByStep[0].citizenship
        const residency = this.formDataByStep[0].residency
        if (this.blackListCountries.includes(residency)) {
          this.showBlackListResidency = true
          return
        }
        if (this.citizenship === 'no_citizenship') {
          this.showHasNoCitizenModal = true
          return
        }
      }
      const lastStepNumber = Object.keys(this.steps).length - 1
      const hasError = await this.saveStep(this.currentStepNumber)
      if (hasError) {
        return
      }
      if (this.currentStepNumber < lastStepNumber) {
        this.currentStepNumber++
        if (this.currentStepNumber === 4) {
          this.currentStepNumber++
        }
      } else {
        await this.$router.push({name: 'register-final-page'})
      }
    },


    async uploadFile(fileData){
      fileData.isUploading = true

      const formData = new FormData()
      formData.append('doc', fileData.file)

      const onUploadProgressCallback = ev => {
        fileData.uploadPercent = Math.round(ev.loaded * 100 / ev.total)
      }

      try {
        const { data } = await api.profile.uploadFile(formData, onUploadProgressCallback)
        Object.assign(fileData, data)
        fileData.newAdded = false

      } catch (e) {
        if(e.response) {
          fileData.error = e.response.data.doc
          fileData.hasError = true
        } else {
          this.$toast.error(this.$i18n.locale === 'uk' ? 'Перевірте підключення до Інтернету': 'Check your internet connection')
          fileData.newAdded = true
          return
        }
      } finally {
        fileData.isUploading = false

      }

    },

    async uploadFiles(){
      const filesToUpload = this.collectNewFilesToUpload()
      await Promise.all(filesToUpload.map(fileData => this.uploadFile(fileData)))
      return filesToUpload
    },

    collectNewFilesToUpload(){
      const filesToUpload = []
      const formDataArray = Array.isArray(this.currentFormData) ? this.currentFormData : [this.currentFormData]

      formDataArray.forEach(formData => {
        for(const key in formData){
          if (key === 'cards') {
            const files = []
            formData.cards.forEach((el, index) => {
              const docs = el.doc.map(doc => {
                doc.field = ['cards', index, 'doc']
                return doc
              })
              files.push(...docs.filter(file => file.newAdded))

            })
            if(files.length) {
              filesToUpload.push(...files)
            }
          }
          if (this.$options.fileFieldNames.includes(key)) {
            const files = formData[key]
            const newFiles = files.map(file =>{
                file.field = key
                return file
              })
              .filter(file => file.newAdded)

            if(newFiles.length) {
              filesToUpload.push(...newFiles)
            }
          }
        }
      })
      return filesToUpload
    },

    async saveStep(stepNumber) {
      this.isLoading = true
      const fileErrors = {}
      try {
        const res = await this.uploadFiles()
        const hasNotLoaded = res.filter(el => !el.id)
        if(hasNotLoaded.length) {
          this.isLoading = false
          return true
        }

        res.filter(el => {
          if (el.hasError) {
            if(Array.isArray(el.field)) {
                fileErrors[el.field[0]] = {[el.field[1]]: {[el.field[2]]: ['']} }
            }else {
              fileErrors[el.field] = ['']
            }
          }
        })
        if (Object.keys(fileErrors).length) {
          this.backendErrors = fileErrors
          throw new Error()

        }
      }catch(err){
        console.log(err)
        this.isLoading = false
        return true
      }

      try {

        const {data} = await api.profile[this.residentStatus][`step${stepNumber}`].post(this.processedCurrentFormData)
        await this.processStepData(data, this.currentStepNumber)
      } catch (e) {
        const errors = e.response.data ?? {}
        this.backendErrors = errors

        if (errors.detail) {
          this.$toast.error(errors.detail)
        }
        if(errors._error_code ) {
          this.$toast.error(errors._error_code)
        }

        if (stepNumber === 5) {
          this.$toast.error(this.$t('notifications.application-is-not-sent'))
        }

        return true
      } finally {
        this.isLoading = false
      }
    },
    async loadStepData(stepNumber) {
      this.isLoading = true
      try {
        const { data } = await api.profile[this.residentStatus][`step${stepNumber}`].get()
        if(stepNumber === 1 && !data.citizenship) {
            this.currentStepNumber = 0
            return
        }
        if(stepNumber === 0 && data.citizenship  && data.residency) {
          this.currentStepNumber = 1
          return
        }
        await this.processStepData(data, stepNumber)

      } catch (e) {
        console.dir(e)
        const error = e.response.data
        if (error._error_code) {
          this.processCodeError(error)
        }
        this.$toast.error(this.$t(e.response.data.detail || 'notifications.something-went-wrong'))
      } finally {
        this.isLoading = false
      }
    },
    async processStepData(data, stepNumber) {

      if (Array.isArray(this.formDataByStep[stepNumber])) {
        this.formDataByStep[stepNumber] = data
        if (stepNumber === 4 && !this.formDataByStep[stepNumber].length) {
          this.addAccount()
        }
      } else {
        const notNullableData = Object.fromEntries(Object.entries(data).filter(([, value]) => value !== null))
        if (stepNumber === 2) {
          const { data } = await api.profile.getPassportTypes()
          this.passportChoices = data

          if (!notNullableData.passport_type) {
            const passportType = this.isResident?
                this.isCitizen? constants.PASSPORT_TYPES.OLD_PASSPORT: constants.PASSPORT_TYPES.TEMPORARY_RESIDENCE  :
                this.isCitizen? constants.PASSPORT_TYPES.FOREIGN_TEMPORARY_RESIDENCE : constants.PASSPORT_TYPES.FOREIGN_CARD_ID
            notNullableData.passport_type = this.passportChoices.find(passport => passport.codename === passportType)?.codename
          } else {
            notNullableData.passport_type = this.passportChoices.find(passport => passport.codename === notNullableData.passport_type)?.codename
          }
        }

        this.$set(this.formDataByStep, stepNumber, {
          ...this.formDataByStep[stepNumber],
          ...notNullableData
        })
      }

      if( stepNumber === 1 && this.formDataByStep[1].has_inn === null){
        this.$set(this.formDataByStep[1], 'has_inn',this.isCitizen)
      }

      if(stepNumber === 4){
        this.formDataByStep[4].forEach((account, index) =>{
          let processedCards = []
          if(account.cards) {
            processedCards = account.cards.map(card => {
              card.id = this.$options.uuidv4()
              if(!card.doc) {
                card.doc = []
              }
              return card
            })
          }
        this.$set(this.formDataByStep[4][index], 'cards', processedCards)
        })
      }
    },
    processCodeError(error) {
      switch (error._error_code) {
        case constants.ERROR_CODES.nonResidentNotAllowed:
          this.showNonResidentModal = true
          break
        case constants.ERROR_CODES.fullSent:
          this.$router.push({name: 'register-final-page', query: {alreadySent: '1'}})
          break

      }
    },
    closeHasNoCitizenModal(){
      window.location.href = process.env.VUE_APP_LANDING_REDIRECT_URL
    }
  }
}
</script>

<style scoped lang="scss">
@import "~@/assets/scss/views/register-common.scss";
</style>
